import { supabase } from '../lib/supabase'

// チュートリアルプロジェクトの設定
const TUTORIAL_PROJECT_NAME = '📚 チュートリアル - TaStacKの使い方'
const TUTORIAL_PROJECT_COLOR = '#6366f1' // Indigo

// チュートリアルタスクの定義
interface TutorialTask {
  title: string
  description: string
  status: 'pending' | 'in_progress' | 'completed'
  display_order: number
}

const TUTORIAL_TASKS: TutorialTask[] = [
  {
    title: '📂 プロジェクトを作成してみよう',
    description: '左側のサイドバー（モバイルは下のメニュー）から「＋プロジェクト追加」をタップして、新しいプロジェクトを作成できます。仕事用・プライベート用など、用途別に分けると便利です。',
    status: 'pending',
    display_order: 1000,
  },
  {
    title: '🎨 プロジェクトボタンを確認しよう',
    description: 'プロジェクト名の右側にあるボタンでプロジェクトを管理できます。\n• 表示設定（全プロジェクトを選択したときに表示するかどうか）\n• プロジェクトカラーの変更\n• チーム共有（共有するチームを選択します）\n• 名前編集\n• プロジェクトを削除',
    status: 'pending',
    display_order: 2000,
  },
  {
    title: '➕ 新しいタスクを追加してみよう',
    description: '画面上部の「＋新しいToDoを追加」ボタンをタップして、新しいタスクを作成してみましょう。タイトル、詳細（任意）、登録先プロジェクト（すべてのプロジェクト選択時のみ）、日付、時間等を入力してEnterキー、または追加ボタンをクリックします。\n※個別プロジェクトを選択している場合は自動的にそのプロジェクトに登録されます。',
    status: 'pending',
    display_order: 3000,
  },
  {
    title: '📝 タスクボタンを確認しよう',
    description: '各タスクの右側にはアイコンボタンがあります。\n• 画像を添付（★＝小さいサムネイル表示、★★＝大きいサムネイル表示）※各タスクごとに1枚サムネイル表示が可能です。\n• ファイルを添付\n• タスクを編集\n• タスクを複製\n• ゴミ箱に移動\nタスクをタップすると詳細編集もできます。',
    status: 'pending',
    display_order: 4000,
  },
  {
    title: '🔀 タスクの並び替えを試そう',
    description: 'タスクの左側のハンドルをドラッグして、タスクの順番を変更できます。重要なタスクを上に持ってきましょう。',
    status: 'pending',
    display_order: 5000,
  },
  {
    title: '🔍 表示切り替えを使おう',
    description: 'タスクリスト上部のフィルターボタンで「未着手」「進行中」「完了」「ゴミ箱」を選んで表示を切り替えられます。ソートボタンでは期限順・作成日順などの並び替えができます。',
    status: 'pending',
    display_order: 6000,
  },
  {
    title: '🎤 AI音声入力を試そう',
    description: 'タスク追加ボタンの横にあるマイクボタンをタップすると、音声でタスクを追加できます。「明日までに報告書を作成」など話しかけると、AIが自動でタスクに変換します。\n\n「○○プロジェクトに××のタスクを金曜日までに、あと△△プロジェクトに◇◇のタスクを3/10までに。」等、複数タスクの一括登録も可能です。',
    status: 'pending',
    display_order: 7000,
  },
  {
    title: '📊 ダッシュボードを確認しよう',
    description: 'ハンバーガーメニューから「ダッシュボード」を開くと、タスクの進捗状況や統計を確認できます。【完了したタスクの履歴】や【ゴミ箱のタスクを完全削除】することもできます。',
    status: 'pending',
    display_order: 8000,
  },
  {
    title: '👥 チーム機能を試そう',
    description: '• プロジェクトの共有ボタンからチームメンバーをメール、QRコード、リンク等で招待します。\n• チームメンバーには「編集者」「閲覧者」などの権限を設定可能です。\n• 招待が承認されるとチーム化が完了です。\n• プロジェクト一覧にある「プロジェクトを共有」から共有したいチームを選択するとプロジェクトの共有が可能です。',
    status: 'pending',
    display_order: 9000,
  },
  {
    title: '🎉 チュートリアル完了！',
    description: 'おめでとうございます！すべての基本機能を学びました。\n\n💡 紹介プログラム：メニューの「紹介プログラム」から紹介した方が有料プランに加入後、30日経過した段階で紹介者・被紹介者の両者に200ptを進呈します。ポイントは1000ptごとにAmazonギフト券1000円分と交換可能です。\n\nこのプロジェクトは削除しても大丈夫です。それでは、TaStacKを使ってタスク管理を始めましょう！',
    status: 'pending',
    display_order: 10000,
  },
]

// 新規ユーザーかどうかをチェック
export async function isNewUser(userId: string): Promise<boolean> {
  try {
    // プロジェクトが1つもない = 新規ユーザー
    const { count, error } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)

    if (error) {
      console.error('Error checking if new user:', error)
      return false
    }

    return (count ?? 0) === 0
  } catch (err) {
    console.error('Error in isNewUser:', err)
    return false
  }
}

const TUTORIAL_CREATED_KEY = 'tastack_tutorial_created'

// チュートリアルプロジェクトとタスクを作成
export async function createTutorialProject(userId: string): Promise<string | null> {
  try {
    // 既に作成済みならスキップ
    if (localStorage.getItem(`${TUTORIAL_CREATED_KEY}_${userId}`) === 'true') {
      console.log('Tutorial already created for this user')
      return null
    }

    // 作成中フラグを立てる（重複実行防止）
    localStorage.setItem(`${TUTORIAL_CREATED_KEY}_${userId}`, 'true')

    // 1. チュートリアルプロジェクトを作成
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .insert({
        name: TUTORIAL_PROJECT_NAME,
        color: TUTORIAL_PROJECT_COLOR,
        user_id: userId,
        display_order: -1000, // 一番最初に表示
        description: 'TaStacKの使い方を学ぶためのチュートリアルプロジェクトです。すべてのタスクを完了してみましょう！',
      })
      .select()
      .single()

    if (projectError) {
      console.error('Error creating tutorial project:', projectError)
      localStorage.removeItem(`${TUTORIAL_CREATED_KEY}_${userId}`)
      return null
    }

    // 2. チュートリアルタスクを作成
    const tasksToInsert = TUTORIAL_TASKS.map(task => ({
      ...task,
      project_id: project.id,
      user_id: userId,
    }))

    const { error: tasksError } = await supabase
      .from('todos')
      .insert(tasksToInsert)

    if (tasksError) {
      console.error('Error creating tutorial tasks:', tasksError)
      // プロジェクトは作成されたが、タスクの作成に失敗
      // プロジェクトを削除して失敗として扱う
      await supabase.from('projects').delete().eq('id', project.id)
      localStorage.removeItem(`${TUTORIAL_CREATED_KEY}_${userId}`)
      return null
    }

    console.log(`Tutorial project created for user ${userId}`)
    return project.id
  } catch (err) {
    console.error('Error in createTutorialProject:', err)
    localStorage.removeItem(`${TUTORIAL_CREATED_KEY}_${userId}`)
    return null
  }
}

// 新規ユーザー向けのデフォルト設定を適用
export function applyNewUserDefaults(): void {
  try {
    // フィルター設定: 完了を含む全ステータスを表示
    // これにより新規ユーザーは完了したタスクも見える
    const defaultFilterStatuses = ['pending', 'in_progress', 'completed']
    localStorage.setItem('todo_filter_statuses', JSON.stringify(defaultFilterStatuses))

    // 初回ログインフラグを設定
    localStorage.setItem('tastack_first_login', 'true')

    console.log('Applied new user defaults')
  } catch (err) {
    console.error('Error applying new user defaults:', err)
  }
}

// チュートリアル完了状態をチェック
export async function isTutorialCompleted(userId: string): Promise<boolean> {
  try {
    // チュートリアルプロジェクトを探す
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('id')
      .eq('user_id', userId)
      .eq('name', TUTORIAL_PROJECT_NAME)
      .maybeSingle()

    if (projectError || !project) {
      return true // プロジェクトがない = 削除済み or 完了
    }

    // 未完了タスクの数を確認
    const { count, error: countError } = await supabase
      .from('todos')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', project.id)
      .neq('status', 'completed')

    if (countError) {
      return false
    }

    return (count ?? 0) === 0
  } catch (err) {
    console.error('Error in isTutorialCompleted:', err)
    return false
  }
}

// 初回ログインかどうかをチェック（localStorage）
export function isFirstLogin(): boolean {
  return localStorage.getItem('tastack_first_login') === 'true'
}

// 初回ログインフラグをクリア
export function clearFirstLoginFlag(): void {
  localStorage.removeItem('tastack_first_login')
}
